import { IMetricOptions, IMetricData, IAddMetric } from "@curefit/metrics-common";
import {
    UserGoalView,
    Goal,
    UserTrackableSymptomUpsertRequest,
    TrackableSymptomViewModel,
    BaseUserInfo
} from "@curefit/gmf-common";
import {
    IMedicinePrescription,
    IPatientAllergy,
    IPatientConcern,
    IPatientExamination,
    IPatientRedFlag,
    IPatientSurgeryAndProcedure,
    IPatientSystemiscExamination,
    IPatientSystemReview,
    IMentalStatusExamination,
    IPastPsychiatricHistory
} from "@curefit/ehr-common";
import { PatientPersonalProfile } from "@curefit/ups-common";
import {
    IPrescription,
    IAppointmentScheduleResponse,
    ISymptom,
    IMedicinePrescription as IMedicinePrescriptionItem,
    IAdvicePrescription,
    ILabtestPrescription
} from "@curefit/care-common";
import PatientLabTestsTab, {
    PatientLabTestsTabProps
} from "../components/ongoing/PatientLabTestsTab";
import { PatientVitalsTabProps } from "../components/ongoing/PatientVitalsTab";
import PatientHistoryTab, {
    PatientHistoryTabProps
} from "../components/ongoing/PatientHistoryTab";
import PhysicalAssessmentTab, {
    PhysicalAssessmentTabProps
} from "../components/ongoing/PhysicalAssessmentTab";
import CommonPatientPrescriptionTab, {
    CommonPatientPrescriptionTabProps
} from "../components/ongoing/CommonPatientPrescriptionTab";
import { IPatientVitalResponse } from "../models/IPatientVitalResponse";
import IVital from "../models/staticData/IVital";
import IPatientVital from "../models/IPatientVital";
import { IEHRSavePatientMedicalProfile } from "../models/patients/IEHRSavePatientMedicalProfile";
import PatientVitalsAndAllergiesTab from "../components/ongoing/PatientVitalsAndAllergiesTab";
import IEditedBy from "../models/IEditedBy";
import PatientChiefComplaintsTab from "../components/ongoing/PatientChiefComplaintsTab";
import IChiefComplaint from "../models/IChiefComplaint";
import IPatientCondition from "../models/IPatientCondition";
import IStaticDataValue from "../../../common/models/IStaticDataValue";
import PatientExaminationsTab from "../components/ongoing/PatientExaminationsTab";
import IinfoQues from "../models/staticData/IinfoQues";
import {
    default as PatientLifestyleTab,
    PatientLifestyleTabProps
} from "../components/ongoing/PatientLifestyleTab";
import IHabitStaticData from "../models/staticData/IHabitStaticData";
import IRoutineActivity from "../models/staticData/IRoutineActivities";
import RoutineAttribute from "../models/staticData/RoutineAttribute";
import IMedicineType from "../models/staticData/IMedicineType";
import ILabTestMaster from "../models/ILabTestMaster";
import { ITypeFormResponse } from "../models/ITypeFormResponse";
import IMentalStatusExaminationStaticDataValue from "../models/staticData/IMentalStatusExaminationStaticDataValue";
import IDiagnosticSuggestions from "../models/IDiagnosticSuggestions";
import IMedicineSuggestions from "../models/IMedicineSuggestions";
import IDietPlan from "../models/IDietPlan";
import {
    default as PatientLifestylePlanTab,
    PatientLifestylePlanTabProps
} from "../components/ongoing/PatientLifestylePlanTab";
import IStaticData from "../models/staticData/IStaticData";
import ModelFactory from "../models/ModelFactory";
import IFoodCategory from "../models/IFoodCategory";
import IFetchMealsByCaloriesRequest from "../models/IFetchMealsByCaloriesRequest";
import IMealItem from "../models/IMealItem";
import IPatientLifestylePlanWrapper from "../models/patients/IPatientLifestylePlanWrapper";
import PatientGoalsTab, {
    PatientGoalsTabProps
} from "../components/ongoing/PatientGoalsTab";
import IDishCatalogueResponse from "../models/maverick/IDishCatalogueResponse";
import IMetric from "../models/metrics/IMetric";
import { ConsultationStatusConstant, PrescriptionTabModes } from "./AppConstants";
import PatientTherapistPlanTab, {
    PatientTherapistPlanTabProps
} from "../components/ongoing/PatientTherapistPlanTab";
import { IPlanMeta } from "../../../common/models/IPlanRequestMeta";
import IDoctorProfile from "../models/IDoctorProfile";
import PatientPlanTab, {
    PatientPlanTabProps
} from "../components/ongoing/PatientPlanUploadTab";
import IOrionPlanResponse from "../models/orion/IOrionPlanResponse";
import PhysioChiefComplaintsTab from "../components/ongoing/PhysioChiefComplaintsTab";
import HeaderUtils from "./HeaderUtils";
import moment = require("moment");
import PatientDiagnosticLabTestsTab, {
    PatientDiagnosticLabTestsTabProps
} from "../components/ongoing/PatientDiagnosticLabTestsTab";
import { IFeatureConfig } from "../models/IFeatureConfig";
import IAppointmentSchedule from "../models/IAppointmentSchedule";
import AGPReportContainer from "../containers/AGPReportContainer";
import { SugarJournalContainer } from "../containers/SugarJournalContainer";
import { InterventionsContainer } from "../containers/InterventionsContainer";

export enum ConsultationTabNames {
    VITALS_AND_ALLERGIES = "VITALS_AND_ALLERGIES",
    LAB_TESTS = "LAB_TESTS",
    VITALS = "VITALS",
    HISTORY = "HISTORY",
    CLINICAL_PLAN = "CLINICAL_PLAN",
    MIND_PLAN = "MIND_PLAN",
    GOALS = "GOALS",
    PRESCRIPTION = "PRESCRIPTION",
    CHIEF_COMPLAINTS = "CHIEF_COMPLAINTS",
    EXAMINATION = "EXAMINATION",
    LIFESTYLE = "LIFESTYLE",
    LIFESTYLE_PLAN = "LIFESTYLE_PLAN",
    UPLOAD_PLAN = "UPLOAD_PLAN",
    PHYSICAL_ASSESSMENT = "PHYSICAL_ASSESSMENT",
    PHYSIO_CHIEF_COMPLAINTS = "PHYSIO_CHIEF_COMPLAINTS",
    DIAGNOSTIC_LAB_TESTS = "DIAGNOSTIC_LAB_TESTS",
    HISTORY_AND_INTAKE = "HISTORY_AND_INTAKE",
    PATIENT_DATA = "PATIENT_DATA",
    ADHERANCE = "ADHERANCE",
    GLUCOSE = "GLUCOSE",
    AGM = "AGM",
}

export interface TabConfig {
    id: string;
    tabName: string;
    display: string;
    component: any;
    childTabs?: TabConfig[];
}

export class TabManager {
    static getLabTestsTabProps = (
        patientId: number,
        curefitUserId: number,
        patientLabTests: ILabTestMaster[],
        nextTab: () => void,
        fetchStaticDiagnosticLabTests: () => Promise<any>,
        savePatientDiagnosticLabTests: (req: IAddMetric) => Promise<any>,
        savePatientLabTestsSuccess: (dataObj: object) => Promise<any>,
        savePatientLabTests: (patientLabTests: any) => void,
        updateDiagnosticLabTests: (diagnosticLabTests: any) => void,
        diagnosticLabTests: any,
        saveEditedPatientLabTests: (diagnosticLabTests: any) => void,
        deletePatientDiagnosticLabTests: (
            deletedLabTestIds: number[]
        ) => Promise<any>,
        manualLabTests: ILabtestPrescription[],
        saveManualLabTests: (diagnosticLabTests: ILabtestPrescription[]) => void
    ): PatientDiagnosticLabTestsTabProps => {
        return {
            patientId: patientId,
            curefitUserId,
            nextTab,
            patientLabTests,
            fetchStaticDiagnosticLabTests,
            savePatientDiagnosticLabTests,
            savePatientLabTestsSuccess,
            savePatientLabTests,
            updateDiagnosticLabTests,
            diagnosticLabTests,
            saveEditedPatientLabTests,
            deletePatientDiagnosticLabTests,
            manualLabTests,
            saveManualLabTests
        };
    };

    static getPatientVitalsAndAllergiesTabProps = (
        userId: number,
        appointmentId: number,
        patientId: number,
        nextTab: () => void,
        staticVitalList: IVital[],
        fetchPatientVitals: (patientId: number) => any,
        savePatientVitals: (patientId: number) => any,
        fetchAllStaticData: () => any,
        editorDetails: IEditedBy,
        saveEditedAllergies: (allergies: IPatientAllergy[]) => void,
        savePatientMedicalProfile: (
            patientId: number,
            medicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number,
            doctorType: string
        ) => void,
        currentAllergies: IPatientAllergy[],
        patientTypeFormData: ITypeFormResponse,
        typeFormMapping: any,
        doctorProfile: IDoctorProfile,
        saveEditedMedicalProfile: (
            patientMedicalProfile: IEHRSavePatientMedicalProfile
        ) => void,
        patientVitals: IPatientVital[],
        uiConfiguration: any[],
        metricIds: number[]
    ) => {
        return {
            userId,
            appointmentId,
            patientId: patientId,
            staticVitalList: staticVitalList,
            fetchPatientVitals: fetchPatientVitals,
            savePatientVitals: savePatientVitals,
            fetchAllStaticData: fetchAllStaticData,
            editedBy: editorDetails,
            //allergies: allergies,
            saveEditedVitalsAndAllergies: saveEditedAllergies,
            savePatientMedicalProfile: savePatientMedicalProfile,
            currentAllergies,
            nextTab,
            patientTypeFormData,
            typeFormMapping,
            doctorProfile,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            patientVitals: patientVitals,
            uiConfiguration,
            metricIds
        };
    };

    static getChiefComplaintsProps = (
        patientId: number,
        editorDetails: IEditedBy,
        patientAllergies: IPatientAllergy[],
        patientVitals: IPatientVitalResponse[],
        patientChiefComplaints: ISymptom[],
        fetchStaticSymptomsData: (input: string) => Promise<any>,
        saveEditedChiefComplaints: (chiefComplaints: IChiefComplaint) => void,
        savePatientMedicalProfile: (patientId: number, patientMedicalProfile: IEHRSavePatientMedicalProfile, appointmentId: number, doctorType: string) => void,
        nextTab: () => void, staticReviewSystemsData: IStaticDataValue[],
        saveReviewOfSystemsData: (data: IPatientSystemReview[]) => void,
        reviewOfSystemsData: IPatientSystemReview[], appointmentId: number,
        doctorType: string, saveEditedPrescription: (prescription: IPrescription) => void,
        prescription: IPrescription, appointmentSchedule: any,
        saveEditedMedicalProfile: (patientMedicalProfile: IEHRSavePatientMedicalProfile) => void,
        patientMedicalProfile: IEHRSavePatientMedicalProfile,
        savePatientMedicalProfileSuccess: (patientId: number, patientMedicalProfile: IEHRSavePatientMedicalProfile) => void,
        isSelf: boolean, uiConfiguration: any[], addSymptomForReview: (name: string) => Promise<any>,
        fetchPatientPastSessions: (patientId: number, subSectionKey: string) => Promise<any>, responseData: any, modalResponse: any,
        suggestions: { [key: string]: any }) => {

        return {
            patientId: patientId,
            editedBy: editorDetails,
            patientAllergies: patientAllergies,
            patientVitals: patientVitals,
            patientChiefComplaints: patientChiefComplaints,
            fetchStaticSymptomsData,
            saveEditedChiefComplaints: saveEditedChiefComplaints,
            savePatientMedicalProfile: savePatientMedicalProfile,
            nextTab: nextTab,
            staticReviewSystemsData,
            saveReviewOfSystemsData,
            reviewOfSystemsData,
            appointmentId,
            doctorType,
            saveEditedPrescription,
            prescription,
            appointmentSchedule,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            currentMedicalProfile: patientMedicalProfile,
            savePatientMedicalProfileSuccess,
            isSelf,
            uiConfiguration,
            addSymptomForReview,
            fetchPatientPastSessions,
            responseData,
            modalResponse,
            suggestions
        };
    };

    static getPhysioChiefComplaintsProps = (
        patientId: number,
        editorDetails: IEditedBy,
        patientAllergies: IPatientAllergy[],
        patientVitals: IPatientVitalResponse[],
        patientChiefComplaints: ISymptom[],
        fetchStaticSymptomsData: (input: string) => Promise<any>,
        saveEditedChiefComplaints: (chiefComplaints: IChiefComplaint) => void,
        saveEditedIllnesses: (illnesses: string) => void, savePatientMedicalProfile: (patientId: number, patientMedicalProfile: IEHRSavePatientMedicalProfile, appointmentId: number, doctorType: string) => void,
        nextTab: () => void, staticReviewSystemsData: IStaticDataValue[], saveReviewOfSystemsData: (data: IPatientSystemReview[]) => void, reviewOfSystemsData: IPatientSystemReview[], appointmentId: number, doctorType: string, suggestions: { [key: string]: any }) => {
        return {
            patientId: patientId,
            editedBy: editorDetails,
            patientAllergies: patientAllergies,
            patientVitals: patientVitals,
            patientChiefComplaints: patientChiefComplaints,
            fetchStaticSymptomsData,
            saveEditedChiefComplaints: saveEditedChiefComplaints,
            saveEditedIllnesses: saveEditedIllnesses,
            savePatientSymptoms: savePatientMedicalProfile,
            nextTab: nextTab,
            staticReviewSystemsData,
            saveReviewOfSystemsData,
            reviewOfSystemsData,
            appointmentId,
            doctorType,
            suggestions
        };
    };

    static getPatientVitalsTabProps = (
        userId: number,
        patientId: number,
        patientVitals: IPatientVitalResponse[],
        staticVitalList: IVital[],
        fetchPatientVitals: (
            patientId: number,
            userId: number,
            metricIds: number[]
        ) => any,
        savePatientVitals: (
            patientId: number,
            patientVitals: IPatientVital[]
        ) => any,
        fetchAllStaticData: () => any,
        metricIds: number[]
    ): PatientVitalsTabProps => {
        return {
            patientId: patientId,
            userId: userId,
            patientVitals: patientVitals,
            staticVitalList: staticVitalList,
            fetchPatientVitals: fetchPatientVitals,
            fetchAllStaticData: fetchAllStaticData,
            savePatientVitals: savePatientVitals,
            metricIds: metricIds
        };
    };

    static getPatientHistoryTabProps = (
        appointmentId: number,
        editorDetails: IEditedBy,
        patientId: number,
        patientAllergies: IPatientAllergy[],
        patientSurgeries: IPatientSurgeryAndProcedure[],
        patientMedication: IMedicinePrescription[],
        nextTab: () => void,
        savePatientMedicalProfile: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number,
            doctorType: string
        ) => Promise<any>,
        patientConditions: IPatientCondition[],
        staticConditions: IStaticDataValue[],
        patientConcerns: IPatientConcern[],
        staticRedFlags: IStaticDataValue[],
        staticMedicineTypes: IMedicineType[],
        patientRedFlags: IPatientRedFlag[],
        saveEditedMedicalProfile: (
            patientMedicalProfile: IEHRSavePatientMedicalProfile
        ) => void,
        patientMedicalProfile: IEHRSavePatientMedicalProfile,
        pastConsultations: IAppointmentScheduleResponse[],
        staticPersonalInfo: IinfoQues,
        patientPersonalProfile: PatientPersonalProfile,
        saveEditedPatientProfile: (profile: PatientPersonalProfile) => void,
        saveAllProfilesForPatient: (
            curefitUserId: number,
            patientId: number,
            isSelf: boolean,
            allProfiles: PatientPersonalProfile
        ) => Promise<any>,
        personalProfileId: number | string,
        fetchStaticSymptomsData: (input: string) => Promise<any>,
        patientTypeFormData: ITypeFormResponse,
        typeFormMapping: any,
        fetchStaticDiseasesData: (
            input: string
        ) => Promise<IDiagnosticSuggestions[]>,
        fetchMedicineByGenericName: (
            name: string
        ) => Promise<IMedicineSuggestions[]>,
        pastPsychiatricHistoryList: IPastPsychiatricHistory[],
        doctorType: string,
        savePatientMedicalProfileSuccess: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile
        ) => void,
        updatePatientProfileData: (
            id: number | string,
            profile: PatientPersonalProfile
        ) => Promise<any>,
        isSelf: boolean,
        curefitUserId: number,
        uiConfiguration: any[],
        staticPatientHabits: IHabitStaticData,
        staticPersonalInfoPhysiotherapist: IinfoQues,
        className: string,
        fetchPastConsultationData: (consultationId: string, doctorType: string, patientId: string) => Promise<any>,
        handleCompleteConsultConfirmation: () => void,
        isCovidActivePack?: boolean
    ): PatientHistoryTabProps => {
        return {
            appointmentId,
            editedBy: editorDetails,
            patientId: patientId,
            patientAllergies: patientAllergies,
            patientSurgeries: patientSurgeries,
            patientMedication: patientMedication,
            savePatientMedicalProfile: savePatientMedicalProfile,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            patientConditions: patientConditions,
            staticConditions: staticConditions,
            patientConcerns: patientConcerns,
            staticRedFlags: staticRedFlags,
            staticMedicineTypes,
            patientRedFlags: patientRedFlags,
            currentMedicalProfile: patientMedicalProfile,
            nextTab: nextTab,
            pastConsultations: pastConsultations,
            staticPersonalInfo: staticPersonalInfo,
            staticPersonalInfoPhysiotherapist: staticPersonalInfoPhysiotherapist,
            patientPersonalProfile: patientPersonalProfile,
            saveEditedPatientProfile: saveEditedPatientProfile,
            updatePatientProfileData: updatePatientProfileData,
            personalProfileId: personalProfileId,
            fetchStaticSymptomsData: fetchStaticSymptomsData,
            patientTypeFormData,
            typeFormMapping,
            fetchStaticDiseasesData,
            fetchMedicineByGenericName,
            pastPsychiatricHistoryList,
            doctorType,
            savePatientMedicalProfileSuccess,
            saveAllProfilesForPatient,
            isSelf,
            curefitUserId,
            uiConfiguration,
            staticPatientHabits,
            className,
            fetchPastConsultationData,
            isCovidActivePack,
            handleCompleteConsultConfirmation
        };
    };

    static getPhysicalAssessmentTabProps = (
        appointmentId: number,
        patientId: number,
        editedBy: IEditedBy,
        nextTab: () => void,
        savePatientMedicalProfile: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number,
            doctorType: string
        ) => Promise<any>,
        saveEditedMedicalProfile: (
            patientMedicalProfile: IEHRSavePatientMedicalProfile
        ) => void,
        patientMedicalProfile: IEHRSavePatientMedicalProfile,
        patientPersonalProfile: PatientPersonalProfile,
        saveEditedPatientProfile: (profile: PatientPersonalProfile) => void,
        personalProfileId: number | string,
        typeFormMapping: any,
        doctorType: string,
        patientTypeFormData: ITypeFormResponse,
        pastPsychiatricHistoryList: IPastPsychiatricHistory[],
        updatePatientProfileData: (
            id: number | string,
            profile: PatientPersonalProfile
        ) => Promise<any>,
        isSelf: boolean,
        curefitUserId: number,
        uiConfiguration: any[],
        doctorProfile: IDoctorProfile,
        fetchPatientPastSessions: (
            patientId: number,
            subSectionKey: string
        ) => Promise<any>
    ): PhysicalAssessmentTabProps => {
        {
            uiConfiguration.map((configdata: any, index: number) => {
                configdata.headers = configdata.headers
                    ? HeaderUtils.generateHeaders(configdata.headers, {
                        assesor: doctorProfile.prefix + " " + doctorProfile.name,
                        date: moment().format("DD MMM, YYYY"),
                        session: "(Current)"
                    })
                    : undefined;
            });
            return {
                appointmentId,
                patientId: patientId,
                editedBy: editedBy,
                savePatientMedicalProfile: savePatientMedicalProfile,
                saveEditedMedicalProfile: saveEditedMedicalProfile,
                currentMedicalProfile: patientMedicalProfile,
                nextTab: nextTab,
                patientPersonalProfile: patientPersonalProfile,
                saveEditedPatientProfile: saveEditedPatientProfile,
                updatePatientProfileData: updatePatientProfileData,
                personalProfileId: personalProfileId,
                patientTypeFormData,
                typeFormMapping,
                pastPsychiatricHistoryList,
                doctorType,
                isSelf,
                curefitUserId,
                uiConfiguration,
                doctorProfile,
                fetchPatientPastSessions
            };
        }
    };

    static getPatientExaminationTabProps = (
        editorDetails: IEditedBy,
        appointmentId: number,
        patientId: number,
        patientVitals: IPatientVitalResponse[],
        patientAllergies: IPatientAllergy[],
        patientExaminations: IPatientExamination[],
        patientSystemicExaminations: IPatientSystemiscExamination[],
        staticGeneralExaminations: IStaticDataValue[],
        staticSystemicExaminations: IStaticDataValue[],
        saveEditedExaminations: (editedExams: IPatientExamination[]) => void,
        saveEditedSystemicExaminations: (
            editedExams: IPatientSystemiscExamination[]
        ) => void,
        savePatientExaminations: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number
        ) => Promise<any>,
        nextTab: () => void,
        pastConsultations: IAppointmentScheduleResponse[],
        mentalStatusExaminations: IMentalStatusExamination[],
        staticMentalStatusExaminations: {
            [key: string]: IMentalStatusExaminationStaticDataValue;
        },
        saveEditedMentalStatusExaminations: (
            editedExams: IMentalStatusExamination[]
        ) => void,
        doctorType: string,
        staticReviewSystemsData: IStaticDataValue[],
        saveReviewOfSystemsData: (data: IPatientSystemReview[]) => void,
        reviewOfSystemsData: IPatientSystemReview[],
        savePatientMedicalProfile: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number,
            doctorType: string
        ) => Promise<any>,
        saveEditedMedicalProfile: (
            patientMedicalProfile: IEHRSavePatientMedicalProfile
        ) => void,
        patientMedicalProfile: IEHRSavePatientMedicalProfile,
        typeFormMapping: any,
        isSelf: boolean,
        uiConfiguration: any[],
        doctorProfile: IDoctorProfile,
        fetchPatientPastSessions: (
            patientId: number,
            subSectionKey: string
        ) => Promise<any>
    ) => {
        return {
            editedBy: editorDetails,
            appointmentId: appointmentId,
            patientId: patientId,
            patientAllergies: patientAllergies,
            patientVitals: patientVitals,
            patientExaminations: patientExaminations,
            patientSystemicExaminations: patientSystemicExaminations,
            generalExamStatics: staticGeneralExaminations,
            systemicExamStatics: staticSystemicExaminations,
            saveEditedExaminations: saveEditedExaminations,
            saveEditedSystemicExaminations: saveEditedSystemicExaminations,
            savePatientExaminations: savePatientExaminations,
            pastConsultations: pastConsultations,
            nextTab: nextTab,
            mentalStatusExaminations,
            staticMentalStatusExaminations,
            saveEditedMentalStatusExaminations,
            doctorType,
            staticReviewSystemsData,
            saveReviewOfSystemsData,
            reviewOfSystemsData,
            savePatientMedicalProfile: savePatientMedicalProfile,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            currentMedicalProfile: patientMedicalProfile,
            typeFormMapping,
            isSelf,
            uiConfiguration,
            doctorProfile,
            fetchPatientPastSessions
        };
    };

    static getPatientPrescriptionTabProps = (editedBy: IEditedBy,
        patientId: number,
        consultationId: number,
        consultationType: string,
        patientVitals: IPatientVitalResponse[],
        patientAllergies: IPatientAllergy[],
        patientComplaints: ISymptom[],
        patientRedFlags: IPatientRedFlag[],
        patientConditions: IPatientCondition[],
        currentMedication: IMedicinePrescriptionItem[],
        prescription: IPrescription,
        saveEditedPrescription: (updatedPrescription: IPrescription) => void,
        fetchStaticDiseaseData: (input: string) => Promise<any>,
        fetchStaticLabTestsData: (input: string) => Promise<any>,
        fetchStaticLifeStyleData: (input: string) => Promise<any>,
        staticSpecialities: any[],
        staticMedicineTypes: IMedicineType[],
        uiConfiguration: any[],
        patientMedicalProfile: IEHRSavePatientMedicalProfile,
        isSelf: boolean,
        curefitUserId: string,
        savePatientMedicalProfile: (patientId: number, patientMedicalProfile: any, appointmentId: number, doctorType: string) => Promise<any>,
        reviewPrescription: (flag: boolean, clearPdfData: boolean) => void,
        doctorType: string,
        updateAppointmentStatus: (bookingId: number, appointmentId: number, status: ConsultationStatusConstant) => Promise<any>,
        saveEditedMedicalProfile: (patientMedicalProfile: IEHRSavePatientMedicalProfile) => void,
        bookingId: number,
        nextTab: () => void,
        updatePhysiotherapyPlanPdf: (file: File) => any,
        suggestions: { [key: string]: any },
        metricIds: number[],
        fetchPatientVitals: (patientId: number, userId: number, metricIds: number[]) => any,
        updateCurrentConsultationData: (medicalProfile: IEHRSavePatientMedicalProfile) => void,
        fetchDataOnSelect: (diagnosis?: Array<string>) => void,
        featureConfigInfo: IFeatureConfig,
        appointmentSchedule: IAppointmentSchedule,
        prescriptionMode: PrescriptionTabModes,
        navigateToDetails?: () => void
    ): CommonPatientPrescriptionTabProps => {
        return {
            editedBy: editedBy,
            patientId: patientId,
            consultationId,
            consultationType: consultationType,
            patientVitals: patientVitals,
            patientAllergies: patientAllergies,
            patientChiefComplaints: patientComplaints,
            patientRedFlags: patientRedFlags,
            patientHistory: patientConditions,
            currentMedication,
            prescription: prescription,
            saveEditedPrescription: saveEditedPrescription,
            reviewPrescription: reviewPrescription,
            fetchStaticDiseaseData,
            fetchStaticLabTestsData,
            fetchStaticLifeStyleData,
            staticSpecialities,
            staticMedicineTypes,
            doctorType,
            updateAppointmentStatus,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            bookingId,
            updatePhysiotherapyPlanPdf,
            uiConfiguration,
            currentMedicalProfile: patientMedicalProfile,
            isSelf,
            curefitUserId,
            savePatientMedicalProfile: savePatientMedicalProfile,
            nextTab,
            activeMedicines: currentMedication,
            suggestions,
            metricIds,
            fetchPatientVitals,
            updateCurrentConsultationData,
            fetchDataOnSelect,
            featureConfigInfo,
            appointmentSchedule,
            prescriptionMode,
            navigateToDetails
        };
    };

    static getPatientLifestyleTabProps = (
        patientId: number,
        curefitUserId: number,
        patientConcerns: IPatientConcern[],
        editedBy: IEditedBy,
        staticPersonalInfo: IinfoQues,
        staticPatientHabits: IHabitStaticData,
        patientPersonalProfile: PatientPersonalProfile,
        saveEditedPatientProfile: (personalInfo: PatientPersonalProfile) => void,
        staticMindfulnessInfo: IinfoQues,
        staticFitnessRecallInfo: IinfoQues,
        fetchStaticSymptomsData: (input: string) => Promise<any>,
        routineActivities: IRoutineActivity[],
        fetchStaticRoutineAttributes: () => Promise<RoutineAttribute>,
        fetchFitnessActivities: (input: string) => Promise<any>,
        fetchMindfulnessActivities: (input: string) => Promise<any>,
        staticFoodTypes: IStaticDataValue[],
        staticCuisines: IStaticDataValue[],
        patientTypeFormData: ITypeFormResponse,
        typeFormMapping: any,
        saveAllProfilesForPatient: (
            curefitUserId: number,
            patientId: number,
            isSelf: boolean,
            allProfiles: PatientPersonalProfile
        ) => Promise<any>,
        fetchDishByDishName: (input: string) => Promise<IDishCatalogueResponse[]>,
        nextTab: () => void,
        isSelf: boolean,
        appointmentSchedule: any,
    ): PatientLifestyleTabProps => {
        return {
            patientId: patientId,
            curefitUserId,
            patientConcerns: patientConcerns,
            editedBy: editedBy,
            staticPersonalInfo: staticPersonalInfo,
            staticPatientHabits: staticPatientHabits,
            patientPersonalProfile: patientPersonalProfile,
            saveEditedPatientProfile: saveEditedPatientProfile,
            staticMindfulnessInfo: staticMindfulnessInfo,
            staticFitnessRecallInfo: staticFitnessRecallInfo,
            fetchStaticSymptomsData: fetchStaticSymptomsData,
            fetchFitnessActivities,
            fetchMindfulnessActivities,
            routineActivities: routineActivities,
            fetchStaticRoutineAttributes: fetchStaticRoutineAttributes,
            staticFoodTypes,
            staticCuisines,
            patientTypeFormData,
            typeFormMapping,
            savePatientProfile: saveAllProfilesForPatient,
            fetchDishByDishName,
            nextTab,
            isSelf,
            appointmentSchedule: appointmentSchedule,
        };
    };

    static getPatientLifestylePlanTabProps = (
        appointmentSchedule: IAppointmentScheduleResponse,
        bookingId: number,
        appointmentId: number,
        secondaryUserId: number,
        patientId: number,
        editedBy: IEditedBy,
        consultationType: string,
        patientFitnessPlan: IPatientConcern[],
        patientDietPlan: IDietPlan,
        staticMealSlots: string[],
        staticMealPreferences: string[],
        fetchFitnessActivities: (input: string) => Promise<any>,
        fetchMindfulnessActivitiesByName: (input: string) => Promise<any>,
        staticFoodGroups: IFoodCategory[],
        fetchStaticFoodGroups: () => Promise<IFoodCategory[]>,
        fetchMealsByCalorie: (
            calorieBreakup: IFetchMealsByCaloriesRequest
        ) => Promise<IMealItem[]>,
        patientPlan: IPatientLifestylePlanWrapper,
        planMeta: IPlanMeta,
        saveEditedPatientProfile: (plan: IPatientLifestylePlanWrapper) => void,
        staticLifestyleHabits: IStaticData,
        reviewPlan: (
            flag: boolean,
            clearPdfData: boolean,
            modalScreen: string
        ) => void,
        fetchDishByDishName: (input: string) => Promise<IDishCatalogueResponse[]>,
        staticSpecialities: any[],
        fetchMealByMealId: (mealId: string) => Promise<IMealItem>,
        previewMeal: (meal: IMealItem) => Promise<IMealItem>,
        createMeal: (meal: IMealItem) => Promise<IMealItem>,
        patientGoals: UserGoalView[],
        patientSecondaryGoals: UserGoalView[],
        patient_interventions: UserGoalView[],
        fetchStaticDietGuidelines: (
            input: string,
            category: string
        ) => Promise<any[]>,
        savePatientPlanInDraft: (
            consultationId: number,
            patientPlan: IOrionPlanResponse
        ) => Promise<any>,
        updateAppointmentStatus: (
            bookingId: number,
            appointmentId: number,
            status: ConsultationStatusConstant
        ) => Promise<any>,
        doctorTypeInAdditionalInstructions: string,
        nextTab: () => void,
        doctorProfile: IDoctorProfile,
        isSelf: boolean,
        consultationId: number,
        patientPlanEligibilityInfo: any,
        staticPatientPlanEligibilityMaskAnswers: IStaticData[],
        updatePatientPlanEligibilityMasks: (
            userId: String,
            secondaryUserId: String,
            isSelf: Boolean,
            eligibilityMasks: String[]
        ) => Promise<any>,
        newConfigData: any,
        staticGoals: Goal,
        staticMetrics: IMetric,
        suggestions: { [key: string]: any },
        appTenant: string,
    ): PatientLifestylePlanTabProps => {
        const patientPlanWithDoctorAdvice: IPatientLifestylePlanWrapper = {
            ...patientPlan
        };
        return {
            appointmentSchedule,
            bookingId,
            appointmentId,
            secondaryUserId: secondaryUserId.toString(),
            patientId: patientId,
            editedBy,
            consultationType,
            patientFitnessPlan: [],
            patientDietPlan: ModelFactory.createNewDietPlanItem(),
            staticMealSlots,
            staticMealPreferences,
            fetchFitnessActivities,
            fetchMindfulnessActivitiesByName,
            staticFoodGroups,
            fetchStaticFoodGroups,
            fetchMealsByCalorie,
            patientPlan: patientPlanWithDoctorAdvice,
            planMeta,
            saveEditedPatientProfile,
            staticLifestyleHabits,
            reviewPlan,
            fetchDishByDishName,
            staticSpecialities,
            fetchMealByMealId,
            previewMeal,
            createMeal,
            patientGoals,
            patientSecondaryGoals,
            patient_interventions: patient_interventions,
            fetchStaticDietGuidelines,
            savePatientPlanInDraft,
            updateAppointmentStatus,
            doctorTypeInAdditionalInstructions,
            nextTab,
            doctorProfile,
            isSelf,
            consultationId,
            patientPlanEligibilityInfo,
            staticPatientPlanEligibilityMaskAnswers,
            updatePatientPlanEligibilityMasks,
            newConfigData,
            staticGoals,
            staticMetrics,
            suggestions,
            appTenant
        };
    };

    static getPatientGoalsTabProps = (
        patientId: number,
        secondaryUserId: string,
        doctorId: number,
        consultationId: number,
        patientGoals: UserGoalView[],
        staticGoals: Goal[],
        staticMetrics: IMetric[],
        fetchPlanStaticData: () => void,
        saveEditedPatientGoals: (patientGoals: UserGoalView[]) => void,
        saveEditedPatientSecondaryGoals: (patientGoals: UserGoalView[]) => void,
        savePatientGoals: (
            patientId: number,
            patientGoals: UserGoalView[]
        ) => Promise<any>,
        savePatientGoalsEdit: (patientId: number, req: any) => Promise<any>,
        nextTab: () => void,
        patientPlan: IPatientLifestylePlanWrapper,
        trackableSymptoms: TrackableSymptomViewModel[],
        patientTrackableSymptoms: UserTrackableSymptomUpsertRequest[],
        saveEditedPatientTrackableSymptoms: (
            trackableSymptoms: UserTrackableSymptomUpsertRequest[]
        ) => void,
        isSelf: boolean,
        doctorType: string,
        savePatientTrackableSymptoms: (
            userId: number,
            reqData: UserTrackableSymptomUpsertRequest[]
        ) => Promise<any>,
        deletePatientTrackableSymptoms: (
            userId: number,
            reqData: UserTrackableSymptomUpsertRequest[]
        ) => Promise<any>,
        addUserMetric: (metricId: string, metricValue: number) => Promise<any>,
        getUserMetrics: (req: IMetricOptions) => void,
        patientMetrics: { [metricId: number]: IMetricData[] }
    ): PatientGoalsTabProps => {
        return {
            patientId,
            secondaryUserId,
            doctorId,
            consultationId,
            patientGoals,
            fetchPlanStaticData,
            staticGoals,
            staticMetrics,
            saveEditedPatientGoals,
            saveEditedPatientSecondaryGoals,
            savePatientGoals,
            savePatientGoalsEdit,
            nextTab,
            patientPlan,
            trackableSymptoms,
            patientTrackableSymptoms,
            saveEditedPatientTrackableSymptoms,
            isSelf,
            doctorType,
            savePatientTrackableSymptoms,
            deletePatientTrackableSymptoms,
            addUserMetric,
            getUserMetrics,
            patientMetrics
        };
    };

    static getPatientTherapistPlanTabProps = (
        editedBy: IEditedBy,
        patientId: number,
        secondaryPatientId: number,
        bookingId: number,
        consultationId: number,
        consultationType: string,
        doctorType: string,
        staticSpecialities: any[],
        staticMedicineTypes: IMedicineType[],
        patientPlan: IPatientLifestylePlanWrapper,
        fetchStaticLabTestsData: (input: string) => Promise<any>,
        patientGoals: UserGoalView[],
        staticGoals: Goal[],
        fetchPlanStaticData: () => any,
        saveEditedPatientProfile: (updatedPatientPlan: IPatientLifestylePlanWrapper) => void,
        reviewPlan: (flag: boolean, clearPdfData: boolean, modalScreen: string) => void,
        updateAppointmentStatus: (bookingId: number, appointmentId: number, status: ConsultationStatusConstant) => Promise<any>,
        fetchStaticSymptomsData: (input: string) => Promise<any>,
        fetchStaticDiseaseData: (input: string) => Promise<any>,
        patientRedFlags: IPatientRedFlag[],
        staticReviewSystemsData: IStaticDataValue[],
        reviewOfSystemsData: IPatientSystemReview[],
        appointmentId: number,
        nextTab: () => void, doctorProfile: IDoctorProfile, pastConsultations: IAppointmentScheduleResponse[],
        currentMedication: IMedicinePrescription[],
        isSelf: boolean, uiConfiguration: any[], patientMedicalProfile: IEHRSavePatientMedicalProfile,
        fetchPatientPastSessions: (patientId: number, subSectionKey: string) => Promise<any>,
        savePatientMedicalProfile: (patientId: number, patientMedicalProfile: IEHRSavePatientMedicalProfile, appointmentId: number, doctorType: string) => Promise<any>,
        patientComplaints: ISymptom[],
        saveEditedMedicalProfile: (patientMedicalProfile: IEHRSavePatientMedicalProfile) => void, responseData: any, modalResponse: any, handleCompleteConsultConfirmation: () => void): PatientTherapistPlanTabProps => {
        return {
            editedBy,
            patientId,
            secondaryPatientId,
            bookingId,
            consultationId,
            consultationType,
            doctorType,
            staticSpecialities,
            staticMedicineTypes,
            patientPlan,
            fetchStaticLabTestsData,
            patientGoals,
            staticGoals,
            fetchPlanStaticData,
            saveEditedPatientProfile,
            reviewPlan,
            updateAppointmentStatus,
            fetchStaticSymptomsData,
            fetchStaticDiseaseData,
            patientRedFlags,
            staticReviewSystemsData,
            reviewOfSystemsData,
            appointmentId,
            nextTab,
            doctorProfile,
            pastConsultations,
            activeMedicines: currentMedication,
            isSelf,
            uiConfiguration,
            currentMedicalProfile: patientMedicalProfile,
            fetchPatientPastSessions,
            savePatientMedicalProfile: savePatientMedicalProfile,
            patientChiefComplaints: patientComplaints,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            responseData,
            modalResponse,
            handleCompleteConsultConfirmation
        }
    }

    static getPlanUploadTabProps = (
        editorDetails: IEditedBy,
        appointmentId: number,
        patientId: number,
        patientVitals: IPatientVitalResponse[],
        patientAllergies: IPatientAllergy[],
        patientExaminations: IPatientExamination[],
        patientSystemicExaminations: IPatientSystemiscExamination[],
        staticGeneralExaminations: IStaticDataValue[],
        staticSystemicExaminations: IStaticDataValue[],
        saveEditedExaminations: (editedExams: IPatientExamination[]) => void,
        saveEditedSystemicExaminations: (
            editedExams: IPatientSystemiscExamination[]
        ) => void,
        savePatientExaminations: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number
        ) => Promise<any>,
        nextTab: () => void,
        pastConsultations: IAppointmentScheduleResponse[],
        mentalStatusExaminations: IMentalStatusExamination[],
        staticMentalStatusExaminations: {
            [key: string]: IMentalStatusExaminationStaticDataValue;
        },
        saveEditedMentalStatusExaminations: (
            editedExams: IMentalStatusExamination[]
        ) => void,
        doctorType: string,
        staticReviewSystemsData: IStaticDataValue[],
        saveReviewOfSystemsData: (data: IPatientSystemReview[]) => void,
        reviewOfSystemsData: IPatientSystemReview[],
        savePatientMedicalProfile: (
            patientId: number,
            patientMedicalProfile: IEHRSavePatientMedicalProfile,
            appointmentId: number,
            doctorType: string
        ) => Promise<any>,
        saveEditedMedicalProfile: (
            patientMedicalProfile: IEHRSavePatientMedicalProfile
        ) => void,
        patientMedicalProfile: IEHRSavePatientMedicalProfile,
        typeFormMapping: any,
        isSelf: boolean,
        uiConfiguration: any[],
        doctorProfile: IDoctorProfile,
        fetchPatientPastSessions: (
            patientId: number,
            subSectionKey: string
        ) => Promise<any>,
        reviewPhysio: (flag: boolean, clearPdfData: boolean) => void,
        suggestions: { [key: string]: any }
    ) => {
        return {
            editedBy: editorDetails,
            appointmentId: appointmentId,
            patientId: patientId,
            patientAllergies: patientAllergies,
            patientVitals: patientVitals,
            patientExaminations: patientExaminations,
            patientSystemicExaminations: patientSystemicExaminations,
            generalExamStatics: staticGeneralExaminations,
            systemicExamStatics: staticSystemicExaminations,
            saveEditedExaminations: saveEditedExaminations,
            saveEditedSystemicExaminations: saveEditedSystemicExaminations,
            savePatientExaminations: savePatientExaminations,
            pastConsultations: pastConsultations,
            nextTab: nextTab,
            mentalStatusExaminations,
            staticMentalStatusExaminations,
            saveEditedMentalStatusExaminations,
            doctorType,
            staticReviewSystemsData,
            saveReviewOfSystemsData,
            reviewOfSystemsData,
            savePatientMedicalProfile: savePatientMedicalProfile,
            saveEditedMedicalProfile: saveEditedMedicalProfile,
            currentMedicalProfile: patientMedicalProfile,
            typeFormMapping,
            isSelf,
            uiConfiguration,
            doctorProfile,
            fetchPatientPastSessions,
            reviewPhysio,
            suggestions
        };
    };

    static getAdheranceTabProps = (showHeader: boolean) => { return { showHeader } }
    static getAGMReportTabProps = (showHeader: boolean) => { return { showHeader } }
    static getGlucoseTabProps = (showHeader: boolean) => { return { showHeader } }

    static ConsultationTabConfig: { [key: string]: TabConfig } = {
        VITALS_AND_ALLERGIES: {
            id: "vitals_and_allergies",
            tabName: ConsultationTabNames.VITALS_AND_ALLERGIES,
            display: "Vitals",
            component: PatientVitalsAndAllergiesTab
        },
        LAB_TESTS: {
            id: "lab_tests",
            tabName: ConsultationTabNames.LAB_TESTS,
            display: "Lab Tests",
            component: PatientDiagnosticLabTestsTab
        },
        CHIEF_COMPLAINTS: {
            id: "Chief Complaints",
            tabName: ConsultationTabNames.CHIEF_COMPLAINTS,
            display: "Chief Complaints",
            component: PatientChiefComplaintsTab
        },
        PHYSIO_CHIEF_COMPLAINTS: {
            id: "Physio Chief Complaints",
            tabName: ConsultationTabNames.PHYSIO_CHIEF_COMPLAINTS,
            display: "Chief Complaints",
            component: PhysioChiefComplaintsTab
        },
        HISTORY: {
            id: "history",
            tabName: ConsultationTabNames.HISTORY,
            display: "History",
            component: PatientHistoryTab
        },
        HISTORY_AND_INTAKE: {
            id: "history",
            tabName: ConsultationTabNames.HISTORY,
            display: "Intake & History",
            component: PatientHistoryTab
        },
        EXAMINATION: {
            id: "examinations",
            tabName: ConsultationTabNames.EXAMINATION,
            display: "Examinations",
            component: PatientExaminationsTab
        },
        PRESCRIPTION: {
            id: "prescription",
            tabName: ConsultationTabNames.PRESCRIPTION,
            display: "Prescription",
            component: CommonPatientPrescriptionTab
        },
        MIND_PLAN: {
            id: "mind_plan",
            tabName: ConsultationTabNames.MIND_PLAN,
            display: "Session Notes",
            component: PatientTherapistPlanTab
        },
        GOALS: {
            id: "goals",
            tabName: ConsultationTabNames.GOALS,
            display: "Goals",
            component: PatientGoalsTab
        },
        LIFESTYLE: {
            id: "lifestyle",
            tabName: ConsultationTabNames.LIFESTYLE,
            display: "Lifestyle Recall",
            component: PatientLifestyleTab
        },
        LIFESTYLE_PLAN: {
            id: "lifestyle_plan",
            tabName: ConsultationTabNames.LIFESTYLE_PLAN,
            display: "Lifestyle Plan",
            component: PatientLifestylePlanTab
        },
        UPLOAD_PLAN: {
            id: "upload_plan",
            tabName: ConsultationTabNames.UPLOAD_PLAN,
            display: "Plan",
            component: PatientPlanTab
        },
        PHYSICAL_ASSESSMENT: {
            id: "physical_assessment",
            tabName: ConsultationTabNames.PHYSICAL_ASSESSMENT,
            display: "Physical Assessment",
            component: PhysicalAssessmentTab
        },
        EDIT_PRESCRIPTION: {
            id: "editPrescription",
            tabName: ConsultationTabNames.PRESCRIPTION,
            display: "Prescription",
            component: CommonPatientPrescriptionTab
        },
        PATIENT_DATA: {
            id: "patient_data",
            tabName: ConsultationTabNames.PATIENT_DATA,
            display: "Patient Data",
            component: null,
            childTabs: [
                {
                    id: "agm",
                    display: "Agm",
                    tabName: ConsultationTabNames.AGM,
                    component: AGPReportContainer
                },
                {
                    id: "glucose",
                    display: "Glucose",
                    tabName: ConsultationTabNames.GLUCOSE,
                    component: SugarJournalContainer
                },
                {
                    id: "adherance",
                    display: "Adherance",
                    tabName: ConsultationTabNames.ADHERANCE,
                    component: InterventionsContainer
                },
                {
                    id: "lab_test",
                    display: "Lab Test",
                    tabName: ConsultationTabNames.LAB_TESTS,
                    component: PatientDiagnosticLabTestsTab
                },
            ]
        }
    };

    static ConsultationToTabListMap: { [key: string]: any[] } = {
        "CONSULTATION": [ConsultationTabNames.HISTORY, ConsultationTabNames.PATIENT_DATA, ConsultationTabNames.EXAMINATION, ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.PRESCRIPTION],
        "KMH": [ConsultationTabNames.LAB_TESTS, ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.HISTORY, ConsultationTabNames.LIFESTYLE],
        "LC": [ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.PATIENT_DATA, ConsultationTabNames.HISTORY, ConsultationTabNames.LIFESTYLE, ConsultationTabNames.LIFESTYLE_PLAN],
        "CONS_MP": [ConsultationTabNames.LAB_TESTS, ConsultationTabNames.HISTORY, ConsultationTabNames.EXAMINATION, ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.PRESCRIPTION],
        "LC_MP": [ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.LAB_TESTS, ConsultationTabNames.HISTORY, ConsultationTabNames.LIFESTYLE, ConsultationTabNames.GOALS, ConsultationTabNames.MIND_PLAN, ConsultationTabNames.LIFESTYLE_PLAN],
        "MIND_THERAPIST": [
            ConsultationTabNames.HISTORY_AND_INTAKE,
            ConsultationTabNames.MIND_PLAN
        ],
        "AI_LC": [ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.LIFESTYLE, ConsultationTabNames.GOALS, ConsultationTabNames.LIFESTYLE_PLAN],
        "PHYSIO": [ConsultationTabNames.VITALS_AND_ALLERGIES, ConsultationTabNames.LAB_TESTS, ConsultationTabNames.CHIEF_COMPLAINTS, ConsultationTabNames.HISTORY, ConsultationTabNames.PHYSICAL_ASSESSMENT, ConsultationTabNames.UPLOAD_PLAN],
        "AYURVEDA": [ConsultationTabNames.LAB_TESTS, ConsultationTabNames.HISTORY, ConsultationTabNames.PRESCRIPTION],
    }
}
